/** Points per action, computed by the club-leaderboard Function (shown in the UI only). */
export type LeaderboardBreakdown = {
  reactions: number;
  comments: number;
  shares: number;
  /** Points carried over from the client-side leaderboard when the server took over. */
  carriedOver: number;
};

/** One user's standing as returned by the club-leaderboard Function. */
export type LeaderboardStandingItem = {
  userId: string;
  displayName: string;
  firstName: string;
  lastName: string;
  profileImageUrl: string | null;
  points: number;
  /** Competition rank (ties share a rank: 1, 1, 3). null when the user has no points this month. */
  rank: number | null;
  /** Total monthly wins (badges), including the ones earned under the old leaderboard. */
  badges: number;
  /** Whether this user won the requested (closed) month. */
  monthBadge: boolean;
  breakdown: LeaderboardBreakdown;
};

export type LeaderboardStandings = {
  period: string;
  timeZone: string;
  periodStart: string;
  periodEnd: string;
  final: boolean;
  computedAt: string | null;
  winnerIds: string[];
  totalCount: number;
  pageNo: number;
  pageSize: number;
  hasNextPage: boolean;
  items: LeaderboardStandingItem[];
  me: LeaderboardStandingItem;
};

export type RankedLeaderboardEntry = {
  userId: string;
  displayName: string;
  avatar: string | null;
  points: number;
  achievements: number;
  /** 0 when the user is not ranked (no points this month). */
  rank: number;
  breakdown?: LeaderboardBreakdown;
};

export const toRankedEntry = (item: LeaderboardStandingItem): RankedLeaderboardEntry => ({
  userId: item.userId,
  displayName:
    item.displayName || [item.firstName, item.lastName].filter(Boolean).join(" ") || item.userId,
  avatar: item.profileImageUrl ?? null,
  points: item.points,
  achievements: item.badges,
  rank: item.rank ?? 0,
  breakdown: item.breakdown,
});

export type LeaderboardView = {
  /** Standings in server rank order (ties share a rank). */
  entries: RankedLeaderboardEntry[];
  /** The signed-in user, also when they are beyond the loaded pages; rank 0 = no points yet. */
  me: RankedLeaderboardEntry | null;
  totalCount: number;
  period: string | null;
  periodEnd: string | null;
};

export const toLeaderboardView = (pages: LeaderboardStandings[]): LeaderboardView => {
  const first = pages[0];
  const seen = new Set<string>();
  const entries: RankedLeaderboardEntry[] = [];
  for (const page of pages)
    for (const item of page.items)
      if (!seen.has(item.userId)) {
        seen.add(item.userId);
        entries.push(toRankedEntry(item));
      }
  return {
    entries,
    me: first?.me ? toRankedEntry(first.me) : null,
    totalCount: first?.totalCount ?? 0,
    period: first?.period ?? null,
    periodEnd: first?.periodEnd ?? null,
  };
};
